import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { APP_RUNTIME_CONFIG } from '@core/auth/app-config.model';

import {
  HandbookSectionDto,
  StoredFileDto,
  UpdateHandbookSectionDto,
} from './handbook.dto';

/**
 * The employee handbook — `/api/v1/handbook`.
 *
 * **What comes back depends on who is asking.** The list answers published
 * sections to everyone and drafts as well to an author, so a reader's shorter list
 * is the rule working, not sections missing. A draft's attachments answer
 * not-found to somebody who may not see the draft, for the same reason.
 */
@Injectable({ providedIn: 'root' })
export class HandbookApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_RUNTIME_CONFIG);

  /** Whether a backend is configured. Empty `apiUrl` = the seeded prototype. */
  available(): boolean {
    return !!this.config.apiUrl;
  }

  /** `apiUrl` already carries the /api/v1 prefix — appending another 404s everything. */
  private url(path = ''): string {
    return `${this.config.apiUrl.replace(/\/$/, '')}/handbook${path}`;
  }

  /** Every section this caller may read. */
  listSections(): Observable<HandbookSectionDto[]> {
    return this.http.get<HandbookSectionDto[]>(this.url());
  }

  /**
   * Edits a section, publishing included.
   *
   * A true patch: absent keys are left alone, so a rename sends `title` by itself
   * and publishing sends `publicationStatus` by itself. There is no updated date to
   * send — the server stamps it on every write.
   */
  updateSection(
    sectionGuid: string,
    body: UpdateHandbookSectionDto,
  ): Observable<HandbookSectionDto> {
    return this.http.patch<HandbookSectionDto>(
      this.url(`/${encodeURIComponent(sectionGuid)}`),
      body,
    );
  }

  /** A section's documents, each with a link signed for this request. */
  listAttachments(sectionGuid: string): Observable<StoredFileDto[]> {
    return this.http.get<StoredFileDto[]>(
      this.url(`/${encodeURIComponent(sectionGuid)}/section-attachments`),
    );
  }

  /** Attaches one document. A POST per file, as everywhere else files are uploaded. */
  attach(sectionGuid: string, file: File): Observable<StoredFileDto> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<StoredFileDto>(
      this.url(`/${encodeURIComponent(sectionGuid)}/section-attachments`),
      form,
    );
  }

  /**
   * Removes an attachment **and the object behind it**. Hard, both of them.
   *
   * Addressed by the FILE's guid rather than through its section, which is why
   * there is no section in this path.
   */
  removeAttachment(fileGuid: string): Observable<void> {
    return this.http.delete<void>(
      this.url(`/section-attachments/${encodeURIComponent(fileGuid)}`),
    );
  }
}
